<script setup>
import { createValidationRules } from "@/utils/validationRules"
import { useI18n } from "vue-i18n"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useModalStore } from '@/stores/ModalStore'
import { showToast, showWarningToast } from "@/utils/toast"
import emitter from "@/utils/emitter"
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useDisplay } from 'vuetify'
import SmsTextarea from "@/components/SmsTextarea.vue"
import SmsTextareaFooter from "@/components/SmsTextareaFooter.vue"
import CustomerSelector from "@/components/event/CustomerSelector.vue"
import { getSmsInfo, SMS_MAX_PARTS } from "@/utils/sms"

const { t } = useI18n()
const { xs } = useDisplay()

const customerStore = useCustomerStore()
const modalStore = useModalStore()
const THIS_MODAL_NAME = 'sendSmsToCustomer'
const validationRules = createValidationRules(t)

const normalize = ref(false) // "replace special characters" - BE gets allowDiacritics = !normalize
// Local states
const isLoadingCustomers = ref(false)
const isSendingSms = ref(false)
const localCustomer = ref(null)
const localCustomersList = ref([])
const message = ref('')
const smsForm = ref()

// New message (no customer in the store): the same CustomerSelector as the visit modal, bound to the store's customer.
// Opened for a customer (visit modal): local search, so changing the recipient doesn't change the visit's customer.
const isNewMessage = !customerStore.selectedCustomer
const recipient = computed(() => isNewMessage ? customerStore.selectedCustomer : localCustomer.value)

watch(recipient, customer => {
  if (customer?.salonerClientId && !customer.phone) showWarningToast(t('smsCustomerHasNoPhone'))
})

const canSend = computed(() => !!recipient.value?.salonerClientId && !!recipient.value.phone &&!!message.value.trim() && getSmsInfo(message.value, normalize.value).parts <= SMS_MAX_PARTS && !isSendingSms.value)

// 👉 Local method not to interfere with the other "CustomerSelectors" while selecting a recipient
const handleLocalSearch = async (searchText) => {
  abortRequestsGetThatStartsWith('/calendar/clients')
  if (!searchText || searchText.length < 3) {
    localCustomersList.value = []

    return
  }
  isLoadingCustomers.value = true
  try {
    const queryParams = new URLSearchParams()
    queryParams.append('term', searchText)

    const response = await $backend(`/calendar/clients?${queryParams.toString()}`, {
      method: 'GET',
    })

    if(response.success) {
      localCustomersList.value = response.data
    } else {
      localCustomersList.value = []
    }

  } catch (error) {
    //console.error('Error searching customers:', error)
    localCustomersList.value = []
  } finally {
    isLoadingCustomers.value = false
  }
}

// 👉 Method to send the SMS message
const sendMessage = async () => {
  isSendingSms.value = true
  const { valid } = await smsForm.value?.validate()
  if (valid) {
    const payload = {
      message: message.value.trim(),
      recipient: Number(recipient.value?.salonerClientId),
      allowDiacritics: !normalize.value,
    }

    try {
      const response = await customerStore.sendMessage(
        payload.recipient,
        payload.message,
        payload.allowDiacritics,
      )
      if(response?.success){
        showToast(t('smsMessageHasBeenSent'), 'success')
        emitter.emit('sms-sent', payload.recipient) // messages page opens the recipient's conversation
        modalStore.closeModal(THIS_MODAL_NAME)
      }
    } catch (error) {
      console.error('Error sending SMS:', error)
    } finally {
      isSendingSms.value = false
    }
  } else {
    isSendingSms.value = false
  }
}


onMounted(() => {
  message.value = ''
  if (customerStore.selectedCustomer) {
    localCustomer.value = { ...customerStore.selectedCustomer }
  }
})
onUnmounted( () => {
  try {
    if(modalStore.focusAfterClose.length) {
      document.getElementById(modalStore.focusAfterClose).focus()
      modalStore.focusAfterClose = ""
    }
  } catch (e) {}
})
</script>

<template>
  <VDialog
    id="smsModal"
    :model-value="modalStore.modals.sendSmsToCustomer"
    :max-width="600"
    scrollable
    :persistent="isSendingSms"
    @update:model-value="(val) => modalStore.modals.sendSmsToCustomer = val"
  >
    <VCard
      :loading="isSendingSms"
      :disabled="isSendingSms"
    >
      <template #title>
        <div class="d-flex align-center justify-space-between ">
          {{ $t('sendSMStoCustomer') }}
          <VIcon
            icon="ri-close-line"
            :disabled="customerStore.isLoading"
            @click="modalStore.closeModal(THIS_MODAL_NAME)"
          />
        </div>
      </template>

      <template #text>
        <VForm
          ref="smsForm"
          validate-on="input"
          class="mt-2"
        >
          <VRow>
            <VCol cols="12">
              <CustomerSelector
                v-if="isNewMessage"
                :show-send-sms-btn="false"
                :show-checkboxes="false"
                :show-client-stats="false"
                :show-client-comment="false"
              />
              <VAutocomplete
                v-else
                v-model="localCustomer"
                class="autocomplete-force-inline"
                autocomplete="off"
                :loading="isLoadingCustomers"
                :items="localCustomersList"
                :item-title="item => item ? `${item.name} ${item.surname}`+ (item.phone ? ` (${item.phone})` : '') : ''"
                :item-value="item => item.ekey"
                density="compact"
                color="secondary"
                :label="$t('customer')"
                :placeholder="$t('type_at_least_3chars')"
                hide-no-data
                auto-select-first
                no-filter
                return-object
                chips
                closable-chips
                disabled
                :rules="[validationRules.required]"
                @update:search="handleLocalSearch"
              />
            </VCol>
            <VCol cols="12">
              <SmsTextarea
                v-model="message"
                :normalize="normalize"
                :placeholder="xs ? $t('messages_page.composer_placeholder_short') : $t('messages_page.composer_placeholder')"
                rows="5"
                max-rows="8"
                auto-grow
                hide-details
              />
              <div class="d-flex align-center flex-wrap gc-4 gr-1 mt-2 text-caption text-high-emphasis">
                <SmsTextareaFooter
                  v-model:normalize="normalize"
                  :text="message"
                  :phone="recipient?.phone"
                />
              </div>
            </VCol>
          </VRow>
        </VForm>
      </template>

      <template #actions>
        <div class="d-flex flex-fill pa-2 justify-space-between">
          <VBtn
            color="secondary"
            variant="outlined"
            :disabled="isSendingSms"
            @click="modalStore.closeModal(THIS_MODAL_NAME)"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            color="green"
            variant="flat"
            :disabled="!canSend"
            @click="sendMessage"
          >
            {{ $t('send') }}
          </VBtn>
        </div>
      </template>
    </VCard>
  </VDialog>
</template>


